import { skipToken } from '@reduxjs/toolkit/query';

import { useCheckCurrentWorkspacePermissionQuery } from '@/features/workspaces/api/workspacesApi';
import {
  getWorkspaceActionGrant,
  type WorkspacePermissionAction,
  type WorkspacePermissionResource,
} from '@/features/workspaces/selectors/permissions';
import { useAppSelector } from '@/store/hooks';

import type {
  WorkspacePermissionCheckPayload,
  WorkspacePermissionCheckResponse,
} from '@/features/workspaces/types';
import type { APIResponse } from '@web/types';

type ScopedPermissionContext = Omit<
  WorkspacePermissionCheckPayload,
  'module' | 'action'
>;

type UseCanScopedWorkspaceActionResult = {
  allowed: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isScoped: boolean;
  scopes: string[];
  data?: APIResponse<WorkspacePermissionCheckResponse>;
};

const SCOPED_PERMISSION_CHECK_RESOURCES = new Set<WorkspacePermissionResource>([
  'job_management',
  'task_management',
  'change_order_management',
]);

export function useCanScopedWorkspaceAction(
  resource: WorkspacePermissionResource,
  action: WorkspacePermissionAction,
  context?: ScopedPermissionContext,
  fallback = false,
): UseCanScopedWorkspaceActionResult {
  const currentWorkspace = useAppSelector(
    (state) => state.workspaces.currentWorkspace,
  );
  const grant = getWorkspaceActionGrant(
    currentWorkspace,
    resource,
    action,
    fallback,
  );
  const isScoped = grant.permission && grant.scopes.length > 0;
  const canCheckScopedPermission = Boolean(
    isScoped && SCOPED_PERMISSION_CHECK_RESOURCES.has(resource) && context,
  );

  const query = useCheckCurrentWorkspacePermissionQuery(
    canCheckScopedPermission
      ? {
          module: resource as WorkspacePermissionCheckPayload['module'],
          action,
          ...context,
        }
      : skipToken,
  );

  if (!grant.permission) {
    return {
      allowed: false,
      isLoading: false,
      isFetching: false,
      isScoped,
      scopes: grant.scopes,
    };
  }

  if (!isScoped) {
    return {
      allowed: true,
      isLoading: false,
      isFetching: false,
      isScoped,
      scopes: grant.scopes,
    };
  }

  if (!canCheckScopedPermission) {
    return {
      allowed: false,
      isLoading: false,
      isFetching: false,
      isScoped,
      scopes: grant.scopes,
    };
  }

  return {
    allowed: query.data?.success === true && query.data.data.allowed,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isScoped,
    scopes: grant.scopes,
    data: query.data,
  };
}
